import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDomainStore } from '@stores/domainStore';
import { apiClient } from '@services/api';
import { useTranslationStore } from '@stores/translationStore';
import { useSessionStore } from '@stores/sessionStore';
import { useSignalR } from '@hooks/useSignalR';
import { playSound } from '../services/soundService';
import PageSpinner from '@components/PageSpinner';
import PageHeader from '@components/PageHeader';
import { emitActivityChanged } from '@utils/activityEvents';
import { promptVerifyToOrder } from '@utils/verifyToOrder';
import VisualCartScanner, { BasketPhoto } from '@components/VisualCartScanner';
import SmartFridge from '@components/SmartFridge';
import './StationOrderPage.scss';

const getHotelSlug = (paramSlug: string | undefined): string | null => {
  if (paramSlug && paramSlug !== 'undefined') return paramSlug;
  return localStorage.getItem('gcxp_hotel_slug');
};

// Types
interface StationOption {
  id: number;
  nameKey: string;
  additionalPrice: number;
  additionalPriceUSD?: number;
  additionalPriceEUR?: number;
  isDefault: boolean;
}

interface StationOptionGroup {
  id: number;
  nameKey: string;
  selectionType?: string;
  minSelections: number;
  maxSelections: number;
  options: StationOption[];
}

interface StationMenuItem {
  id: number;
  nameKey: string;
  descriptionKey?: string;
  imagePath?: string;
  price?: number;
  priceUSD?: number;
  priceEUR?: number;
  isIncludedInMeal: boolean;
  maxQuantity?: number;
  stockQuantity?: number;
  optionGroups: StationOptionGroup[];
}

interface StationCategory {
  id: number;
  nameKey: string;
  descriptionKey?: string;
  imagePath?: string;
  maxItemsPerGuestPerDay?: number;
  items: StationMenuItem[];
}

interface ProgressStep {
  step: number;
  label: string;
  labelKey?: string;
  isComplete: boolean;
  isCurrent: boolean;
}

interface OrderItemSummary {
  menuItemId: number;
  nameKey: string;
  imagePath?: string;
  quantity: number;
  unitPrice: number;
  lineTotal: number;
}

interface StationMenuResponse {
  stationId: number;
  stationName: string;
  stationNameKey?: string;
  stationDescriptionKey?: string;
  stationIcon?: string;
  stationImage?: string;
  trackingMode: string;
  fulfillmentMode?: string;
  /** Self-checkout: 'SelfCarting' (guest photos), 'ShelfTracking' (smart fridge) or 'Both' */
  cartCapture?: string;
  /** Self carting: 'GuestPhone' | 'CounterCamera' | 'Both' */
  photoSource?: string;
  counterCameras?: { id: string; name: string; isOnline: boolean }[];
  welcomeMessage?: string;
  thankYouMessage?: string;
  thankYouMessageKey?: string;
  preparingMessage?: string;
  preparingMessageKey?: string;
  serviceStartTime?: string;
  serviceEndTime?: string;
  isServiceActive: boolean;
  acceptsOrdersWhenClosed: boolean;
  acceptsPreOrders: boolean;
  preOrderDaysBefore: number;
  preOrderCutoffTime?: string;
  maxOrdersPerDay: number;
  maxOrdersPerSession: number;
  ordersPlacedToday: number;
  canOrderMore: boolean;
  folioOrdersEnabled: boolean;
  freeOrdersRemaining: number;
  isBeyondFreeLimit: boolean;
  guestName: string;
  roomNumber: string;
  guestCount: number;
  usedGuestNames: string[];
  hotelName: string;
  currencySymbol?: string;
  currencyCode?: string;
  guestType?: string;
  preferredCurrency?: string;
  usdExchangeRate?: number;
  eurExchangeRate?: number;
  categories: StationCategory[];
}

interface StationOrderResponse {
  orderId: string;
  status: string;
  trackingMode: string;
  fulfillmentMode?: string;
  items: OrderItemSummary[];
  totalAmount: number;
  currencySymbol?: string;
  message: string;
  messageKey?: string;
  preparingMessage?: string;
  preparingMessageKey?: string;
  estimatedWaitMinutes: number;
  tableNumber?: number;
  orderedByName?: string;
  preOrderDate?: string;
  createdAt: string;
  progress?: ProgressStep[];
}

// Cart item (local state for item selection)
interface CartItem {
  menuItem: StationMenuItem;
  quantity: number;
  selectedOptionIds: number[];
  notes?: string;
}

export default function StationOrderPage() {
  const { hotelSlug: paramSlug, stationId: stationIdParam } = useParams<{ hotelSlug: string; stationId: string }>();
  const [searchParams] = useSearchParams();
  const hotelSlug = getHotelSlug(paramSlug);
  const stationId = parseInt(stationIdParam || '0', 10);
  const { loadTranslations, t, language } = useTranslationStore();
  const { personalName, isDeviceTrusted } = useSessionStore();

  const [view, setView] = useState<'selection' | 'placing' | 'tracking' | 'done'>('selection');
  const [menu, setMenu] = useState<StationMenuResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The server's reason a paid order was refused (card_required → offer to save a card).
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getBasePath } = useDomainStore();
  const [sharedSlotConfirm, setSharedSlotConfirm] = useState<{ message: string; remainingSlots: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Editing/cancelling a pending order
  const [editingOrderId, setEditingOrderId] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);

  // Cart
  const [cart, setCart] = useState<CartItem[]>([]);
  const [specialNotes, setSpecialNotes] = useState('');

  // Self-checkout (Visual Cart): basket photos survive closing the scanner, so "add a photo" rescans all of them
  const [showScanner, setShowScanner] = useState(false);
  const [basketPhotos, setBasketPhotos] = useState<BasketPhoto[]>([]);
  const [visualCartScanId, setVisualCartScanId] = useState<string | null>(null);

  const orderedByName = personalName || '';
  const isSelfCheckout = menu?.fulfillmentMode === 'SelfCheckout';
  // Fridge only → the fridge screen. Both → the fridge card on top of the normal scan-and-cart screen.
  const isShelfTracking = isSelfCheckout && menu?.cartCapture === 'ShelfTracking';
  const hasFridgeToo = isSelfCheckout && menu?.cartCapture === 'Both';
  // Self carting with a counter camera: the one this guest scanned the QR of, else the first online one.
  const counterParam = searchParams.get('counter');
  const onlineCounters = (menu?.counterCameras ?? []).filter(c => c.isOnline);
  const counterCamera = isSelfCheckout && !isShelfTracking && menu?.photoSource !== 'GuestPhone'
    ? (onlineCounters.find(c => c.id === counterParam) ?? onlineCounters[0] ?? null)
    : null;
  // The phone camera stays available unless the station is counter-only AND the counter is up.
  const allowPhonePhotos = menu?.photoSource !== 'CounterCamera' || !counterCamera;

  // Arrived by scanning the counter's QR: open the scanner straight away (once).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (counterParam && counterCamera && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setShowScanner(true);
    }
  }, [counterParam, counterCamera]);
  // Same rule the server charges by (FolioChargeHelper.ResolveOrderCurrencyAsync / PickMenuItemPrice):
  // a foreign guest who prefers USD or EUR pays in it; everyone else pays in the hotel's currency.
  const isForeigner = menu?.guestType === 'foreigner';
  const preferred = (menu?.preferredCurrency || '').toUpperCase();
  const guestCurrency: 'USD' | 'EUR' | null = isForeigner && (preferred === 'USD' || preferred === 'EUR') ? preferred : null;
  const sym = guestCurrency === 'USD' ? '$' : guestCurrency === 'EUR' ? '€' : (menu?.currencySymbol || '₪');

  // Resolve the display price for an item in the guest's currency
  const resolvePrice = (item: StationMenuItem): number | undefined => {
    if (!guestCurrency) return item.price;
    const own = guestCurrency === 'USD' ? item.priceUSD : item.priceEUR;
    if (own != null) return own;
    const rate = guestCurrency === 'USD' ? menu?.usdExchangeRate : menu?.eurExchangeRate;
    if (item.price != null && rate && rate > 0) {
      return Math.round((item.price / rate) * 100) / 100;
    }
    return item.price;
  };

  // Table number from QR ?t= param
  const qrTable = searchParams.get('t');
  const [tableNumber, setTableNumber] = useState<number | null>(() => {
    if (qrTable) return parseInt(qrTable, 10) || null;
    return null;
  });

  useEffect(() => {
    if (qrTable) {
      const num = parseInt(qrTable, 10);
      if (num > 0) setTableNumber(num);
    }
  }, [qrTable]);

  // Order tracking
  const [currentOrder, setCurrentOrder] = useState<StationOrderResponse | null>(null);
  const { on } = useSignalR();
  const currentOrderIdRef = useRef<string | null>(null);

  // Listen for real-time status updates
  useEffect(() => {
    const unsub = on('OrderStatusChanged', (data) => {
      const { orderId, status } = data as { orderId: string; stationId?: number; status: string };
      if (orderId && currentOrderIdRef.current === orderId) {
        if (status === 'Ready' || status === 'Delivered') {
          playSound('order-ready');
        } else {
          playSound('notification');
        }
        // Re-fetch order details
        apiClient.get(`/api/v1/${hotelSlug}/stations/${stationId}/order/${orderId}`)
          .then(res => setCurrentOrder(res.data))
          .catch(err => console.error('Failed to refresh order:', err));
      }
    });
    return unsub;
  }, [on, hotelSlug, stationId]);

  // Fetch menu data
  useEffect(() => {
    if (hotelSlug && stationId > 0) {
      fetchMenu();
    }
    if (hotelSlug && language) {
      loadTranslations(hotelSlug, 'orders', language);
    }
  }, [hotelSlug, stationId, language]);

  const fetchMenu = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<StationMenuResponse>(`/api/v1/${hotelSlug}/stations/${stationId}/menu`);
      setMenu(response.data);

      // Only check for existing orders if user has a name set
      // This allows different people in the same room to place orders
      if (response.data.ordersPlacedToday > 0 && orderedByName) {
        await checkExistingOrder();
      }
    } catch (err: unknown) {
      console.error('Failed to fetch station menu:', err);
      const e = err as { response?: { status?: number; data?: { Message?: string; message?: string; Error?: string; error?: string } } };
      // Show actual error from API for better debugging
      const apiError = e.response?.data?.Message || e.response?.data?.message
        || e.response?.data?.Error || e.response?.data?.error
        || (e.response?.status === 403 ? 'Access denied to this station' : null);
      setError(apiError || t('station_order.unableLoad') || 'Unable to load menu');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingOrder = async () => {
    try {
      // Only check orders for this specific guest name (not all session orders)
      if (!orderedByName) return;
      
      const response = await apiClient.get(`/api/v1/${hotelSlug}/stations/${stationId}/orders?name=${encodeURIComponent(orderedByName)}`);
      if (response.data.length > 0) {
        const latestOrder = response.data[0];
        if (latestOrder.status !== 'Delivered' && latestOrder.status !== 'Cancelled' && latestOrder.status !== 'Completed') {
          setCurrentOrder(latestOrder);
          if (latestOrder.trackingMode === 'Tracked') {
            setView('tracking');
          } else {
            setView('done');
          }
          currentOrderIdRef.current = latestOrder.orderId;
        }
      }
    } catch (err) {
      console.error('Failed to check existing orders:', err);
    }
  };

  // Cart management
  const addToCart = useCallback((item: StationMenuItem) => {
    setCart(prev => {
      const existing = prev.find(c => c.menuItem.id === item.id);
      if (existing) {
        const maxQty = item.maxQuantity || 10;
        if (existing.quantity >= maxQty) return prev;
        return prev.map(c => c.menuItem.id === item.id ? { ...c, quantity: c.quantity + 1 } : c);
      }
      // Set default options
      const defaultOptionIds: number[] = [];
      item.optionGroups.forEach(og => {
        og.options.filter(o => o.isDefault).forEach(o => defaultOptionIds.push(o.id));
      });
      return [...prev, { menuItem: item, quantity: 1, selectedOptionIds: defaultOptionIds }];
    });
  }, []);

  const removeFromCart = useCallback((itemId: number) => {
    setCart(prev => {
      const existing = prev.find(c => c.menuItem.id === itemId);
      if (existing && existing.quantity > 1) {
        return prev.map(c => c.menuItem.id === itemId ? { ...c, quantity: c.quantity - 1 } : c);
      }
      return prev.filter(c => c.menuItem.id !== itemId);
    });
  }, []);

  const getCartQuantity = (itemId: number) => cart.find(c => c.menuItem.id === itemId)?.quantity || 0;

  const cartItemCount = cart.reduce((sum, c) => sum + c.quantity, 0);
  const cartTotal = cart.reduce((sum, c) => sum + (resolvePrice(c.menuItem) || 0) * c.quantity, 0);

  // This order will post a folio charge (beyond the free allowance, folio enabled). Free orders are
  // NOT gated — only chargeable ones require a trusted device.
  // Self-checkout is a shop: any priced item is charged (the server applies the same rule).
  const orderWillCharge = isSelfCheckout
    ? cartTotal > 0
    : !!(menu?.isBeyondFreeLimit && menu?.folioOrdersEnabled);
  const showPrices = isSelfCheckout || !!(menu?.isBeyondFreeLimit && menu?.folioOrdersEnabled);
  // The server would refuse this charge (folio off, or the stay can't charge) — don't offer the button.
  const selfCheckoutCantCharge = isSelfCheckout && cartTotal > 0 && !menu?.folioOrdersEnabled;

  // The guest confirmed a scan: the basket becomes the cart (a rescan replaces it, never adds to it).
  const applyScan = (lines: { menuItemId: number; quantity: number }[], scanId: string) => {
    const allItems = menu?.categories.flatMap(c => c.items) ?? [];
    const next: CartItem[] = [];
    for (const line of lines) {
      const mi = allItems.find(i => i.id === line.menuItemId);
      if (!mi) continue;
      const defaultOptionIds: number[] = [];
      mi.optionGroups.forEach(og => og.options.filter(o => o.isDefault).forEach(o => defaultOptionIds.push(o.id)));
      next.push({ menuItem: mi, quantity: line.quantity, selectedOptionIds: defaultOptionIds });
    }
    setCart(next);
    setVisualCartScanId(scanId);
    setShowScanner(false);
  };

  const clearBasket = () => {
    basketPhotos.forEach(p => { if (p.blob) URL.revokeObjectURL(p.url); });
    setBasketPhotos([]);
    setVisualCartScanId(null);
  };

  const handlePlaceOrder = async (confirmSharedSlot = false) => {
    if (cart.length === 0) return;

    // Chargeable order on a browse-only device: clear the room-phone gate first, then retry.
    // isDeviceTrusted() reads live store state, so the retry won't loop on a stale closure value.
    if (orderWillCharge && !isDeviceTrusted()) {
      promptVerifyToOrder(() => handlePlaceOrder(confirmSharedSlot));
      return;
    }

    try {
      setSubmitting(true);
      setView('placing');
      setSharedSlotConfirm(null);
      const body = {
        items: cart.map(c => ({
          menuItemId: c.menuItem.id,
          quantity: c.quantity,
          notes: c.notes || undefined,
          optionIds: c.selectedOptionIds.length > 0 ? c.selectedOptionIds : undefined
        })),
        notes: specialNotes || undefined,
        tableNumber: tableNumber || undefined,
        orderedByName: orderedByName || undefined,
        confirmUseSharedSlot: confirmSharedSlot || undefined,
        visualCartScanId: isSelfCheckout && visualCartScanId ? visualCartScanId : undefined
      };

      // Editing an existing pending order → PUT; otherwise place a new one → POST
      const response = editingOrderId
        ? await apiClient.put<StationOrderResponse>(`/api/v1/${hotelSlug}/stations/${stationId}/order/${editingOrderId}`, body)
        : await apiClient.post<StationOrderResponse>(`/api/v1/${hotelSlug}/stations/${stationId}/order`, body);

      setEditingOrderId(null);
      setCurrentOrder(response.data);
      currentOrderIdRef.current = response.data.orderId;
      playSound('order-sent');

      // Self-checkout orders are complete on placement — nothing to track.
      if (response.data.trackingMode === 'Tracked' && !isSelfCheckout) {
        setView('tracking');
      } else {
        setView('done');
      }
      setCart([]);
      if (isSelfCheckout) clearBasket();
      emitActivityChanged();
    } catch (err: unknown) {
      console.error('Failed to place order:', err);
      const e = err as { response?: { status?: number; data?: { requiresConfirmation?: boolean; error?: string | { message?: string }; Error?: string; remainingSessionSlots?: number; code?: string } } };

      // Paid lines need a verified device: verify, then retry the same order.
      if (e.response?.data?.code === 'verify') {
        setView('selection');
        promptVerifyToOrder(() => handlePlaceOrder(confirmSharedSlot));
        return;
      }

      // Handle shared-slot confirmation (409 with RequiresConfirmation)
      if (e.response?.status === 409 && e.response?.data?.requiresConfirmation) {
        setSharedSlotConfirm({
          message: (e.response.data.error as string) || e.response.data.Error || 'Personal limit reached. Use another guest\'s slot?',
          remainingSlots: e.response.data.remainingSessionSlots ?? 0
        });
        setView('selection');
        return;
      }

      const rawError = e.response?.data?.error;
      const errorMessage = (typeof rawError === 'object' ? (rawError as { message?: string })?.message : rawError as string)
        || e.response?.data?.Error
        || t('station_order.orderFailed') || 'Failed to place order';
      setError(errorMessage);
      setErrorCode(e.response?.data?.code ?? null);
      setView('selection');
    } finally {
      setSubmitting(false);
    }
  };

  // Re-open a pending order in the selection view with its items pre-loaded, to edit it.
  const handleEditOrder = () => {
    if (!currentOrder || !menu) return;
    const allItems = menu.categories.flatMap(c => c.items);
    const rebuilt: CartItem[] = [];
    for (const oi of currentOrder.items) {
      const mi = allItems.find(i => i.id === oi.menuItemId);
      if (!mi) continue;
      const defaultOptionIds: number[] = [];
      mi.optionGroups.forEach(og => og.options.filter(o => o.isDefault).forEach(o => defaultOptionIds.push(o.id)));
      rebuilt.push({ menuItem: mi, quantity: oi.quantity, selectedOptionIds: defaultOptionIds });
    }
    setCart(rebuilt);
    setEditingOrderId(currentOrder.orderId);
    setCancelConfirm(false);
    setError(null);
    setView('selection');
  };

  const handleCancelOrder = async () => {
    if (!currentOrder) return;
    try {
      setActionBusy(true);
      await apiClient.delete(`/api/v1/${hotelSlug}/stations/${stationId}/order/${currentOrder.orderId}`);
      setCancelConfirm(false);
      setCurrentOrder(null);
      currentOrderIdRef.current = null;
      setCart([]);
      setSpecialNotes('');
      setEditingOrderId(null);
      setView('selection');
      emitActivityChanged();
      await fetchMenu();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { Error?: string; error?: string } } };
      setError(e.response?.data?.Error || e.response?.data?.error || t('station_order.cancelFailed') || 'Failed to cancel order');
      setCancelConfirm(false);
    } finally {
      setActionBusy(false);
    }
  };

  // Edit / Cancel buttons shown while an order is still Pending (kitchen hasn't started it).
  const renderPendingActions = () => {
    if (!currentOrder || currentOrder.status !== 'Pending') return null;
    return (
      <div className="pending-actions" style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button
          onClick={handleEditOrder}
          disabled={actionBusy}
          style={{ flex: 1, padding: '10px 12px', background: '#fff', color: '#1E40AF', border: '1px solid #3B82F6', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
        >
          ✏️ {t('station_order.editOrder') || 'Edit Order'}
        </button>
        {!cancelConfirm ? (
          <button
            onClick={() => setCancelConfirm(true)}
            disabled={actionBusy}
            style={{ flex: 1, padding: '10px 12px', background: '#fff', color: '#B91C1C', border: '1px solid #EF4444', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            🗑️ {t('station_order.cancelOrder') || 'Cancel Order'}
          </button>
        ) : (
          <button
            onClick={handleCancelOrder}
            disabled={actionBusy}
            style={{ flex: 1, padding: '10px 12px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            {actionBusy ? '…' : (t('station_order.confirmCancel') || 'Tap to confirm')}
          </button>
        )}
      </div>
    );
  };

  const resolveText = (key?: string, fallback?: string) => {
    if (!key) return fallback || '';
    const translated = t(key);
    return translated !== key ? translated : (fallback || key);
  };

  // ── Render states ──

  if (loading) {
    return <PageSpinner />;
  }

  if (error && !menu) {
    return (
      <div className="station-order-page station-error">
        <div className="error-content">
          <span className="error-icon">⚠️</span>
          <h2>{t('station_order.oops') || 'Oops!'}</h2>
          <p>{error}</p>
          <button onClick={fetchMenu} className="retry-btn">{t('common.tryAgain') || 'Try Again'}</button>
        </div>
      </div>
    );
  }

  // Service closed
  if (menu && !menu.isServiceActive && !menu.acceptsOrdersWhenClosed) {
    return (
      <div className="station-order-page station-closed">
        <div className="closed-content">
          <span className="moon-icon">🌙</span>
          <h2>{t('station_order.closed') || 'Station Closed'}</h2>
          <p>{t('station_order.closedHours') || 'Come back during service hours'}</p>
          {menu.serviceStartTime && menu.serviceEndTime && (
            <p className="service-hours">{menu.serviceStartTime} - {menu.serviceEndTime}</p>
          )}
        </div>
      </div>
    );
  }

  // Tracking view (Tracked mode)
  if (view === 'tracking' && currentOrder && currentOrder.progress) {
    return (
      <div className="station-order-page station-tracking">
        <PageHeader
          title={<>{menu?.stationIcon || '🍽️'} {t('station_order.yourOrder') || 'Your Order'}</>}
          subtitle={currentOrder.orderedByName ? `👤 ${currentOrder.orderedByName}` : undefined}
        />

        <div className="order-card">
          <div className="order-summary">
            <div className="items-list">
              {currentOrder.items.map((item, idx) => (
                <span key={idx} className="item-badge">{item.quantity}× {resolveText(item.nameKey)}</span>
              ))}
            </div>
            <div className="order-meta">
              {currentOrder.tableNumber && (
                <p className="order-table">🪑 {t('station_order.table') || 'Table'} {currentOrder.tableNumber}</p>
              )}
              <p className="order-time">{t('station_order.ordered') || 'Ordered'} {formatTime(currentOrder.createdAt)}</p>
            </div>
          </div>

          <div className="progress-tracker">
            {currentOrder.progress?.map((step, index) => (
              <div
                key={step.step}
                className={`progress-step ${step.isComplete ? 'complete' : ''} ${step.isCurrent ? 'current' : ''}`}
              >
                <div className="step-indicator">{step.isComplete ? '✓' : step.step}</div>
                <div className="step-content">
                  <span className="step-label">{resolveText(step.labelKey, step.label)}</span>
                </div>
                {index < (currentOrder.progress?.length ?? 0) - 1 && (
                  <div className={`step-connector ${step.isComplete ? 'complete' : ''}`} />
                )}
              </div>
            ))}
          </div>

          {currentOrder.status === 'Ready' && (
            <div className="status-alert ready">
              <span>🔔</span>
              <p>{
                menu?.fulfillmentMode === 'TableSeating'
                  ? (t('station_order.readyTable') || 'Your order is on its way to your table!')
                  : menu?.fulfillmentMode === 'RoomDelivery'
                    ? (t('station_order.readyRoom') || 'Your order is being delivered to your room!')
                    : (t('station_order.readyPickup') || 'Your order is ready for pickup!')
              }</p>
            </div>
          )}

          {currentOrder.status === 'Delivered' && (
            <div className="status-alert delivered">
              <span>🎉</span>
              <p>{t('station_order.enjoy') || 'Enjoy your meal!'}</p>
            </div>
          )}

          {renderPendingActions()}

          {/* Allow placing another order if room limit not reached */}
          {menu?.canOrderMore && (
            <button
              className="new-order-btn"
              onClick={() => {
                setCurrentOrder(null);
                currentOrderIdRef.current = null;
                setView('selection');
                setCart([]);
              }}
            >
              {t('station_order.placeAnother') || 'Place Another Order'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Simple mode "done" view — same card design as the laundry page (no live progress steps).
  if (view === 'done' && currentOrder) {
    const doneMsg = isSelfCheckout
      ? resolveText('visual_cart.done', currentOrder.totalAmount > 0 ? 'All set — charged to your room. Enjoy!' : 'All set. Enjoy!')
      : currentOrder.messageKey
        ? resolveText(currentOrder.messageKey, currentOrder.message)
        : (currentOrder.message || t('station_order.thankYou') || 'Thank you!');
    return (
      <div className="station-order-page station-tracking">
        <PageHeader
          title={<>{menu?.stationIcon || '🍽️'} {t('station_order.yourOrder') || 'Your Order'}</>}
          subtitle={currentOrder.orderedByName ? `👤 ${currentOrder.orderedByName}` : undefined}
        />

        <div className="order-card">
          <div className="order-summary">
            <div className="items-list">
              {currentOrder.items.map((item, idx) => (
                <span key={idx} className="item-badge">{item.quantity}× {resolveText(item.nameKey)}</span>
              ))}
            </div>
            <div className="order-meta">
              {currentOrder.tableNumber && (
                <p className="order-table">🪑 {t('station_order.table') || 'Table'} {currentOrder.tableNumber}</p>
              )}
              <p className="order-time">{t('station_order.ordered') || 'Ordered'} {formatTime(currentOrder.createdAt)}</p>
            </div>
          </div>

          <div className="status-alert confirmed">
            <span>✅</span>
            <p>{doneMsg}</p>
          </div>

          {renderPendingActions()}

          {/* Allow placing another order if room limit not reached */}
          {menu?.canOrderMore && (
            <button
              className="new-order-btn"
              onClick={() => {
                setCurrentOrder(null);
                currentOrderIdRef.current = null;
                setView('selection');
                setCart([]);
              }}
            >
              {t('station_order.placeAnother') || 'Place Another Order'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Daily limit reached (all guests combined) — only block if folio not enabled.
  // Never block while editing an existing pending order (no new order is being created).
  if (menu && !menu.canOrderMore && !editingOrderId) {
    return (
      <div className="station-order-page station-limit">
        <div className="limit-content">
          <span className="check-icon">✅</span>
          <h2>{t('station_order.allDone') || 'All done for today!'}</h2>
          <p>{(menu.guestCount || 1) > 1
            ? (t('station_order.maxOrdersReachedRoom') || `All ${menu.guestCount} guests have reached their daily order limit.`)
            : (t('station_order.maxOrdersReached') || 'You have reached your daily order limit.')}</p>
        </div>
      </div>
    );
  }

  // Placing animation
  if (view === 'placing') {
    const preparingMsg = menu?.preparingMessageKey
      ? resolveText(menu.preparingMessageKey, menu.preparingMessage || 'Getting everything ready')
      : (menu?.preparingMessage || t('station_order.preparing') || 'Getting everything ready');
    return (
      <div className="station-order-page station-placing">
        <div className="placing-content">
          <div className="spin-icon">{menu?.stationIcon || '🍽️'}</div>
          <h2>{t('station_order.sending') || 'Sending your order...'}</h2>
          <p>{preparingMsg}</p>
        </div>
      </div>
    );
  }

  // ── Selection view ──
  const categories = menu?.categories || [];

  // Smart fridge: no cart to build — the guest opens the fridge and the shelf cameras see what was taken.
  if (isShelfTracking && hotelSlug) {
    return (
      <div className="station-order-page station-selection">
        <PageHeader
          title={<>{menu?.stationIcon || '🧊'} {resolveText(menu?.stationNameKey, menu?.stationName)}</>}
          subtitle={menu?.welcomeMessage}
        />
        <SmartFridge
          hotelSlug={hotelSlug}
          stationId={stationId}
          items={categories.flatMap(c => c.items).map(i => ({
            id: i.id,
            name: resolveText(i.nameKey),
            price: resolvePrice(i),
            imagePath: i.imagePath,
          }))}
          currencySymbol={sym}
          t={t}
        />
      </div>
    );
  }

  return (
    <div className="station-order-page station-selection">
      <PageHeader
        title={<>{menu?.stationIcon || '🍽️'} {resolveText(menu?.stationNameKey, menu?.stationName)}</>}
        subtitle={menu?.welcomeMessage}
        trailing={isSelfCheckout
          ? <button className="vc-header-btn" onClick={() => setShowScanner(true)} aria-label={t('visual_cart.title') || 'Scan your items'}>📷</button>
          : orderedByName ? <span className="guest-name-badge">👤 {orderedByName}</span> : undefined}
      />

      {hasFridgeToo && hotelSlug && (
        <div style={{ marginBottom: '1rem' }}>
          <SmartFridge
            hotelSlug={hotelSlug}
            stationId={stationId}
            items={categories.flatMap(c => c.items).map(i => ({
              id: i.id,
              name: resolveText(i.nameKey),
              price: resolvePrice(i),
              imagePath: i.imagePath,
            }))}
            currencySymbol={sym}
            t={t}
            showMenu={false}
          />
        </div>
      )}

      {isSelfCheckout && (
        <button className="vc-scan-banner" onClick={() => setShowScanner(true)}>
          <span className="vc-scan-icon">📷</span>
          <span>
            <strong>{resolveText('visual_cart.bannerTitle', cart.length > 0 && visualCartScanId ? 'Scan again' : 'Scan your items')}</strong>
            <span>{resolveText('visual_cart.bannerNote', 'Take a photo of what you picked — we add it to your cart. Or add items from the menu below.')}</span>
          </span>
        </button>
      )}

      {isSelfCheckout && !menu?.folioOrdersEnabled && (
        <div className="inline-error">
          <span>⚠️</span> {resolveText('visual_cart.noFolio', 'Charging to your room isn’t available for this stay. Please pay at reception.')}
        </div>
      )}

      {showScanner && hotelSlug && (
        <VisualCartScanner
          hotelSlug={hotelSlug}
          stationId={stationId}
          items={categories.flatMap(c => c.items).map(i => ({
            id: i.id,
            name: resolveText(i.nameKey),
            price: resolvePrice(i),
            imagePath: i.imagePath,
            stockQuantity: i.stockQuantity,
          }))}
          currencySymbol={sym}
          photos={basketPhotos}
          onPhotosChange={setBasketPhotos}
          counterCamera={counterCamera}
          allowPhonePhotos={allowPhonePhotos}
          onApply={applyScan}
          onClose={() => setShowScanner(false)}
          t={t}
        />
      )}

      {editingOrderId && (
        <div className="editing-banner" style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>✏️</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#1E40AF', fontSize: 14 }}>{t('station_order.editingOrder') || 'Editing your order'}</strong>
            <p style={{ color: '#1D4ED8', fontSize: 12, margin: 0 }}>{t('station_order.editingNote') || 'Add new items below or adjust quantities, then tap Update Order.'}</p>
          </div>
          <button
            onClick={() => {
              setEditingOrderId(null);
              setCart([]);
              setView(currentOrder?.progress ? 'tracking' : (currentOrder ? 'done' : 'selection'));
            }}
            style={{ background: 'transparent', border: 'none', color: '#1E40AF', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}
            aria-label={t('common.cancel') || 'Cancel'}
          >×</button>
        </div>
      )}

      {menu && !menu.isServiceActive && menu.acceptsOrdersWhenClosed && (
        <div className="next-shift-banner" style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>🌙</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#92400E', fontSize: 14 }}>{t('station_order.nextShift') || 'Order for next shift'}</strong>
            {menu.serviceStartTime && (
              <p style={{ color: '#A16207', fontSize: 12, margin: 0 }}>
                {t('station_order.nextShiftNote') || `Service starts at ${menu.serviceStartTime}`}
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="inline-error">
          <span>⚠️</span> {error}
          {errorCode === 'card_required' && (
            <button className="inline-error__action" onClick={() => navigate(`${getBasePath()}/folio`)}>
              {t('station_order.saveCard')}
            </button>
          )}
          <button onClick={() => { setError(null); setErrorCode(null); }}>×</button>
        </div>
      )}

      {/* Shared slot confirmation dialog */}
      {sharedSlotConfirm && (
        <div className="shared-slot-confirm" style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: '12px 14px', margin: '0 0 12px' }}>
          <p style={{ color: '#92400E', fontSize: 14, margin: '0 0 10px', fontWeight: 500 }}>
            ⚠️ {sharedSlotConfirm.message}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              style={{ flex: 1, padding: '8px 12px', background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
              onClick={() => handlePlaceOrder(true)}
            >
              {t('common.confirm') || 'Confirm'}
            </button>
            <button
              style={{ flex: 1, padding: '8px 12px', background: '#fff', color: '#92400E', border: '1px solid #F59E0B', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}
              onClick={() => setSharedSlotConfirm(null)}
            >
              {t('common.cancel') || 'Cancel'}
            </button>
          </div>
        </div>
      )}

      {/* Folio charge notice when beyond free limit */}
      {menu?.isBeyondFreeLimit && menu?.folioOrdersEnabled && (
        <div className="folio-charge-banner" style={{ background: '#EFF6FF', border: '1px solid #3B82F6', borderRadius: 8, padding: '10px 14px', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>🧾</span>
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#1E40AF', fontSize: 14 }}>{t('station_order.folioChargeTitle') || 'Orders charged to your folio'}</strong>
            <p style={{ color: '#1D4ED8', fontSize: 12, margin: 0 }}>
              {t('station_order.folioChargeNote') || 'Your complimentary orders have been used. Additional orders will be charged to your room folio.'}
            </p>
          </div>
        </div>
      )}

      {/* Table Number - only show for TableSeating mode */}
      {(!menu?.fulfillmentMode || menu.fulfillmentMode === 'TableSeating') && (
      <section className="station-section table-section">
        <h2>🪑 {t('station_order.tableNumber') || 'Table Number'}</h2>
        <div className="table-selector">
          <input
            type="number"
            className="table-input"
            min={1}
            max={99}
            placeholder={t('station_order.enterTable') || 'Enter table #'}
            value={tableNumber ?? ''}
            onChange={(e) => {
              const val = e.target.value ? parseInt(e.target.value, 10) : null;
              setTableNumber(val && val > 0 ? val : null);
            }}
          />
          {tableNumber && (
            <button className="table-clear" onClick={() => setTableNumber(null)}>×</button>
          )}
        </div>
      </section>
      )}

      {/* Menu Categories & Items */}
      {categories.map(cat => (
        <section key={cat.id} className="station-section category-section">
          <h2>{resolveText(cat.nameKey)}</h2>
          {cat.descriptionKey && <p className="category-desc">{resolveText(cat.descriptionKey)}</p>}
          <div className="items-grid">
            {cat.items.map(item => {
              const qty = getCartQuantity(item.id);
              const maxQty = item.maxQuantity || 10;
              return (
                <div key={item.id} className={`item-card ${qty > 0 ? 'selected' : ''}`}>
                  <div className="item-visual">
                    {item.imagePath ? (
                      <img src={item.imagePath} alt="" className="item-img" />
                    ) : (
                      <span className="item-placeholder">🍽️</span>
                    )}
                  </div>
                  <div className="item-info">
                    <span className="item-name">{resolveText(item.nameKey)}</span>
                    {item.descriptionKey && (
                      <span className="item-desc">{resolveText(item.descriptionKey)}</span>
                    )}
                    {resolvePrice(item) != null && resolvePrice(item)! > 0 && (
                      !item.isIncludedInMeal || showPrices
                    ) && (
                      <span className="item-price">{sym}{resolvePrice(item)!.toFixed(2)}</span>
                    )}
                    {!isSelfCheckout && item.isIncludedInMeal && item.price != null && item.price > 0 && !menu?.isBeyondFreeLimit && (
                      <span className="item-included" style={{ fontSize: 11, color: '#16A34A', fontWeight: 500 }}>
                        {t('station_order.included') || '✓ Included'}
                      </span>
                    )}
                  </div>
                  <div className="item-qty-controls">
                    {qty > 0 ? (
                      <>
                        <button className="qty-btn minus" onClick={() => removeFromCart(item.id)}>−</button>
                        <span className="qty-display">{qty}</span>
                        <button
                          className="qty-btn plus"
                          onClick={() => addToCart(item)}
                          disabled={qty >= maxQty || (item.stockQuantity != null && qty >= item.stockQuantity)}
                        >+</button>
                      </>
                    ) : item.stockQuantity != null && item.stockQuantity <= 0 ? (
                      <span className="out-of-stock-badge" style={{ fontSize: 12, color: '#9CA3AF', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {t('station_order.outOfStock') || 'Out of stock'}
                      </span>
                    ) : (
                      <button
                        className="add-btn"
                        onClick={() => addToCart(item)}
                      >
                        {t('station_order.add') || 'Add'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {/* Notes */}
      {cart.length > 0 && (
        <section className="station-section notes-section">
          <h2>{t('station_order.notes') || 'Special Notes'}</h2>
          <textarea
            className="notes-input"
            placeholder={t('station_order.notesPlaceholder') || 'Any special requests?'}
            value={specialNotes}
            onChange={(e) => setSpecialNotes(e.target.value)}
            maxLength={200}
          />
        </section>
      )}

      {/* Order Button */}
      <div className="order-footer">
        {orderWillCharge && menu?.folioOrdersEnabled && cart.length > 0 && (
          <p className="folio-charge-hint" style={{ color: '#1D4ED8', fontSize: 12, textAlign: 'center', margin: '0 0 6px' }}>
            🧾 {t('station_order.folioChargeHint') || 'This order will be charged to your room folio'}
          </p>
        )}
        <button
          className="order-btn"
          disabled={cart.length === 0 || submitting || selfCheckoutCantCharge}
          onClick={() => handlePlaceOrder()}
        >
          <span className="btn-icon">{cart.length > 0 && orderWillCharge && !isDeviceTrusted() ? '🔒' : (menu?.stationIcon || '🍽️')}</span>
          <span className="btn-text">
            {cart.length === 0
              ? (t('station_order.selectItems') || 'Select items to order')
              : selfCheckoutCantCharge
                ? resolveText('visual_cart.cantCharge', 'Room charging unavailable — pay at reception')
              : orderWillCharge && !isDeviceTrusted()
                ? (t('verify.verifyToCharge') || 'Verify to charge to room')
                : isSelfCheckout
                  ? `${cartTotal > 0 ? resolveText('visual_cart.chargeToRoom', 'Charge to room') : resolveText('visual_cart.confirm', 'Confirm')} (${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'}${cartTotal > 0 ? ` · ${sym}${cartTotal.toFixed(2)}` : ''})`
                  : `${editingOrderId ? (t('station_order.updateOrder') || 'Update Order') : (t('station_order.placeOrder') || 'Place Order')} (${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'}${menu?.isBeyondFreeLimit && menu?.folioOrdersEnabled && cartTotal > 0 ? ` · ${sym}${cartTotal.toFixed(2)}` : ''})`}
          </span>
        </button>
      </div>
    </div>
  );
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
